import { useState, useEffect, useCallback } from "react"
import { ExternalLink } from "lucide-react"
import type { PageCursor, UserProfile } from "@ezbatna/core/domain/models"
import type { AdminReport } from "@ezbatna/core"
import { getContainer } from "../lib/container"
import { Button, ConfirmDialog, DataTable, StatusPill, useToast, type DataTableColumn } from "../components/ui"

const PAGE_SIZE = 20
const APP_URL = "https://app.ezbatna.com"

const targetTypeLabel: Record<AdminReport["targetType"], string> = {
  service: "خدمة",
  comment: "تعليق",
  rating: "تقييم",
}

const statusConfig: Record<NonNullable<AdminReport["status"]>, { label: string; variant: "neutral" | "warning" | "success" }> = {
  new: { label: "جديد", variant: "warning" },
  reviewed: { label: "قيد المراجعة", variant: "neutral" },
  resolved: { label: "تم الحل", variant: "success" },
}

interface ResolvedTarget {
  contentPreview: string
  ownerId: string | null
  viewUrl: string | null
  missing: boolean
}

export function ReportsPage() {
  const [reports, setReports] = useState<AdminReport[]>([])
  const [targets, setTargets] = useState<Record<string, ResolvedTarget>>({})
  const [owners, setOwners] = useState<Record<string, UserProfile>>({})
  const [cursor, setCursor] = useState<PageCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirmTakedown, setConfirmTakedown] = useState<AdminReport | null>(null)
  const [confirmBan, setConfirmBan] = useState<{ report: AdminReport; owner: UserProfile } | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const toast = useToast()

  const resolveTargets = useCallback(async (list: AdminReport[]) => {
    const container = getContainer()
    const entries = await Promise.all(
      list.map(async (r): Promise<[string, ResolvedTarget]> => {
        try {
          if (r.targetType === "service") {
            const service = await container.serviceRepository.fetchById(r.targetId)
            return [r.id, service
              ? { contentPreview: service.title, ownerId: service.userId, viewUrl: `${APP_URL}/services/${service.id}`, missing: false }
              : { contentPreview: "الخدمة محذوفة", ownerId: null, viewUrl: null, missing: true }]
          }
          if (r.targetType === "comment") {
            const comment = await container.commentsRepository.fetchById(r.targetId)
            return [r.id, comment
              ? { contentPreview: comment.text, ownerId: comment.userId, viewUrl: `${APP_URL}/services/${comment.serviceId}`, missing: false }
              : { contentPreview: "التعليق محذوف", ownerId: null, viewUrl: null, missing: true }]
          }
          const rating = await container.ratingsRepository.fetchById(r.targetId)
          return [r.id, rating
            ? { contentPreview: rating.comment || `تقييم ${rating.score}/10 بدون تعليق`, ownerId: rating.raterUserId, viewUrl: `${APP_URL}/user/${rating.ratedUserId}`, missing: false }
            : { contentPreview: "التقييم محذوف", ownerId: null, viewUrl: null, missing: true }]
        } catch {
          return [r.id, { contentPreview: "تعذّر تحميل المحتوى", ownerId: null, viewUrl: null, missing: true }]
        }
      }),
    )
    setTargets((prev) => ({ ...prev, ...Object.fromEntries(entries) }))

    const ownerIds = Array.from(new Set(entries.map(([, t]) => t.ownerId).filter((id): id is string => !!id)))
    const missingOwnerIds = ownerIds.filter((id) => !owners[id])
    if (missingOwnerIds.length > 0) {
      const profiles = await Promise.all(missingOwnerIds.map((id) => container.userProfileRepository.fetchProfile(id)))
      setOwners((prev) => {
        const next = { ...prev }
        profiles.forEach((p, i) => {
          if (p) next[missingOwnerIds[i]!] = p
        })
        return next
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchReports = async (after?: PageCursor | null) => {
    setLoading(true)
    try {
      const page = await getContainer().reportsRepository.fetchPage(PAGE_SIZE, after)
      setReports((prev) => (after ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
      resolveTargets(page.items)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTakedown = async () => {
    if (!confirmTakedown) return
    setActionLoading(true)
    try {
      await getContainer().adminRepository.takedownTarget(confirmTakedown.targetType, confirmTakedown.targetId)
      setReports((prev) => prev.filter((r) => r.id !== confirmTakedown.id))
      toast.show("تم حذف الهدف المُبلَّغ عنه", "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setActionLoading(false)
      setConfirmTakedown(null)
    }
  }

  const handleBan = async () => {
    if (!confirmBan) return
    setActionLoading(true)
    try {
      await getContainer().adminRepository.setUserDisabled(confirmBan.owner.id, true)
      toast.show(`تم حظر ${confirmBan.owner.name ?? confirmBan.owner.id}`, "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setActionLoading(false)
      setConfirmBan(null)
    }
  }

  const columns: DataTableColumn<AdminReport>[] = [
    { key: "targetType", header: "النوع", render: (r) => targetTypeLabel[r.targetType] },
    {
      key: "content",
      header: "المحتوى المُبلَّغ عنه",
      render: (r) => {
        const t = targets[r.id]
        if (!t) return <span className="text-caption text-text-secondary">جارٍ التحميل…</span>
        return (
          <div className="flex max-w-[280px] items-start gap-xs">
            <span className={t.missing ? "text-caption italic text-text-secondary" : "line-clamp-2"}>{t.contentPreview}</span>
            {t.viewUrl && (
              <a href={t.viewUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-text-secondary hover:text-accent" onClick={(e) => e.stopPropagation()}>
                <ExternalLink className="size-3.5" />
              </a>
            )}
          </div>
        )
      },
    },
    {
      key: "owner",
      header: "المستخدم المسؤول",
      render: (r) => {
        const t = targets[r.id]
        const owner = t?.ownerId ? owners[t.ownerId] : null
        if (!t) return null
        if (!t.ownerId) return <span className="text-caption text-text-secondary">—</span>
        if (!owner) return <span className="font-mono text-caption text-text-secondary">{t.ownerId.slice(0, 10)}…</span>
        return (
          <a
            href={`${APP_URL}/user/${owner.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {owner.name ?? "—"} <span className="text-text-secondary">@{owner.username}</span>
          </a>
        )
      },
    },
    { key: "reason", header: "السبب", render: (r) => <span className="line-clamp-2 max-w-[220px]">{r.reason}</span> },
    {
      key: "status",
      header: "الحالة",
      render: (r) => {
        const status = r.status ?? "new"
        const config = statusConfig[status]
        return <StatusPill variant={config.variant}>{config.label}</StatusPill>
      },
    },
    {
      key: "createdAt",
      header: "التاريخ",
      align: "end",
      render: (r) => <span className="text-caption text-text-secondary">{r.createdAt?.toLocaleDateString("ar-SA") ?? "—"}</span>,
    },
    {
      key: "actions",
      header: "إجراءات",
      align: "center",
      render: (r) => {
        const t = targets[r.id]
        const owner = t?.ownerId ? owners[t.ownerId] : null
        return (
          <div className="flex flex-wrap items-center justify-center gap-xs">
            {!t?.missing && (
              <Button variant="destructive" size="sm" onClick={() => setConfirmTakedown(r)}>
                حذف الهدف
              </Button>
            )}
            {owner && (
              <Button variant="secondary" size="sm" className="border-danger/30 text-danger hover:bg-danger/5" onClick={() => setConfirmBan({ report: r, owner })}>
                حظر المستخدم
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">التقارير</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">مراجعة البلاغات المقدمة من المستخدمين</p>
      </div>

      <DataTable
        columns={columns}
        rows={reports}
        rowKey={(r) => r.id}
        loading={loading}
        emptyTitle="لا توجد بلاغات"
        emptyDescription="لم يتم استلام أي بلاغات حتى الآن."
      />

      {cursor && (
        <Button variant="secondary" onClick={() => fetchReports(cursor)} loading={loading} className="self-start">
          عرض المزيد
        </Button>
      )}

      <ConfirmDialog
        open={!!confirmTakedown}
        title="حذف الهدف المُبلَّغ عنه؟"
        description={
          <>
            سيتم حذف هذا الـ
            {" "}
            {confirmTakedown ? targetTypeLabel[confirmTakedown.targetType] : ""}
            {" "}
            نهائياً من التطبيق. هذا الإجراء لا يمكن التراجع عنه.
          </>
        }
        confirmLabel="حذف نهائي"
        destructive
        loading={actionLoading}
        onConfirm={handleTakedown}
        onCancel={() => setConfirmTakedown(null)}
      />

      <ConfirmDialog
        open={!!confirmBan}
        title="حظر هذا المستخدم؟"
        description={
          confirmBan && (
            <>
              لن يتمكن «{confirmBan.owner.name ?? confirmBan.owner.id}» (@{confirmBan.owner.username}) من تسجيل الدخول أو استخدام
              التطبيق بعد الحظر. يمكن التراجع عن هذا الإجراء لاحقاً من صفحة المستخدمين.
            </>
          )
        }
        confirmLabel="حظر الحساب"
        destructive
        loading={actionLoading}
        onConfirm={handleBan}
        onCancel={() => setConfirmBan(null)}
      />
    </div>
  )
}
